/**
 * O seletor de cliente ou de fornecedor do formulário de lançamento.
 *
 * Busca por nome, CPF ou CNPJ e devolve o CÓDIGO (`C0001` / `F0001`), nunca o
 * uuid — CONSTITUICAO.md §2: o usuário digita e vê o código, jamais o id.
 *
 * A lista de resultados é um BLOCO abaixo do campo, e não um popover
 * posicionado. A gaveta rola, e popover `absolute` dentro de ancestral com
 * `overflow` é exatamente como o menu da tabela ficou cortado antes de virar
 * portal. Aqui o empurrão de layout é previsível e não custa nada — são no
 * máximo seis linhas.
 *
 * Depois de escolher, o campo some e sobra a pessoa escolhida com "Trocar":
 * manter o texto de busca ao lado do resultado é o caminho curto para alguém
 * achar que precisa buscar de novo.
 */
import { useEffect, useState } from 'react'

import { Icone } from '../../componentes/Icone'
import { MensagemDeErro } from '../../componentes/Campos'
import { carregarClientes } from '../../plataforma/clientes'
import type { PessoaResumo, TipoLancamento } from '../../plataforma/financeiro'
import { carregarFornecedores } from '../../plataforma/fornecedores'

/** Quantos resultados cabem sem a lista virar uma segunda tela. */
const TETO = 6

export function BuscaDePessoa({
  tipo,
  escolhida,
  aoEscolher,
  erro,
}: {
  tipo: TipoLancamento
  /** A pessoa já escolhida, quando há uma. */
  escolhida: PessoaResumo | null
  aoEscolher: (pessoa: PessoaResumo | null) => void
  erro?: string
}) {
  const rotulo = tipo === 'receita' ? 'Cliente' : 'Fornecedor'
  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [achados, setAchados] = useState<PessoaResumo[] | null>(null)
  const [falhou, setFalhou] = useState(false)

  // Debounce: uma consulta por tecla numa base de clientes é como se enche a
  // fila de requisição enquanto alguém digita o nome inteiro.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca.trim()), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    if (escolhida || buscaAtiva.length < 2) {
      setAchados(null)
      return
    }
    const controle = new AbortController()
    const consulta =
      tipo === 'receita'
        ? carregarClientes(buscaAtiva, 'todos', controle.signal).then((dados) =>
            (dados.linhas ?? []).map((c) => ({ codigo: c.codigo, nome: c.nome })),
          )
        : carregarFornecedores(buscaAtiva, 'todos', controle.signal).then((dados) =>
            (dados.linhas ?? []).map((f) => ({
              codigo: f.codigo,
              nome: f.nome_fantasia || f.nome,
            })),
          )

    consulta
      .then((linhas) => {
        setAchados(linhas.slice(0, TETO))
        setFalhou(false)
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setAchados([])
        setFalhou(true)
      })
    return () => controle.abort()
  }, [tipo, buscaAtiva, escolhida])

  // Trocar de tipo troca de cadastro: um cliente escolhido não continua válido
  // quando o lançamento vira despesa.
  useEffect(() => {
    setBusca('')
    setBuscaAtiva('')
    setAchados(null)
  }, [tipo])

  if (escolhida) {
    return (
      <div>
        <p className="mb-2 text-[13px] font-semibold text-texto">{rotulo} *</p>
        <div className="flex items-center justify-between gap-3 rounded-sistema border border-borda bg-fundo px-3.5 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-[13.5px] font-semibold text-tinta">{escolhida.nome}</p>
            <p className="tabular text-[12px] text-texto-suave">{escolhida.codigo}</p>
          </div>
          <button
            type="button"
            onClick={() => aoEscolher(null)}
            className="shrink-0 rounded-sistema px-2 py-1 text-[12.5px] font-semibold text-marca hover:bg-marca-fundo"
          >
            Trocar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <label
        htmlFor={`busca-pessoa-${tipo}`}
        className="mb-2 block text-[13px] font-semibold text-texto"
      >
        {rotulo} *
      </label>
      <div className="composto h-[38px]" aria-invalid={erro ? true : undefined}>
        <Icone nome="search" tamanho={17} className="text-texto-fraco" />
        <input
          id={`busca-pessoa-${tipo}`}
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          placeholder="Busque por nome, CPF ou CNPJ"
          aria-invalid={erro ? true : undefined}
          autoComplete="off"
        />
      </div>

      {erro ? (
        <MensagemDeErro>{erro}</MensagemDeErro>
      ) : (
        <p className="mt-1.5 text-[12.5px] text-texto-fraco">Busque por nome, CPF ou CNPJ</p>
      )}

      {achados !== null && (
        <div
          role="group"
          aria-label={`Resultados de ${rotulo.toLowerCase()}`}
          className="mt-1.5 overflow-hidden rounded-sistema border border-borda"
        >
          {achados.length === 0 ? (
            <p className="px-3.5 py-2.5 text-[12.5px] text-texto-suave">
              {falhou
                ? 'Não foi possível buscar agora. Tente de novo.'
                : `Nenhum ${rotulo.toLowerCase()} para “${buscaAtiva}”.`}
            </p>
          ) : (
            achados.map((pessoa) => (
              <button
                key={pessoa.codigo}
                type="button"
                onClick={() => aoEscolher(pessoa)}
                className="flex w-full items-baseline justify-between gap-3 border-t border-divisoria px-3.5 py-2.5 text-left first:border-t-0 hover:bg-divisoria"
              >
                <span className="min-w-0 truncate text-[13.5px] text-tinta">{pessoa.nome}</span>
                <span className="tabular shrink-0 text-[12px] text-texto-suave">
                  {pessoa.codigo}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
